import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Logout, Settings, User } from '@carbon/icons-react';
import { CdAppShellTemplate } from '@ui';
// import { CdAppShellTemplate, CdNotificationsPanel } from '@ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, ScrollRestoration, useLocation, useMatches, useNavigate } from 'react-router';
import { paths } from '@/app/router/paths';
import type { RouteHandle } from '@/app/router/handle';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { buildNavItems } from '@/app/navigation/nav.config';
import { useLanguage } from '@/i18n/useLanguage';
import {
  selectMobileNavOpen,
  selectNavMode,
  setMobileNavOpen,
  setNavMode,
} from '@/app/navigation/navSlice';
// import {
//   closeNotifications,
//   openNotifications,
//   selectNotificationFilter,
//   selectNotificationsOpen,
//   setNotificationFilter,
// } from '@/modules/notifications/notificationsSlice';
// import { useNotifications } from '@/modules/notifications/queries';
import { useCurrentUser } from '@/modules/account/queries';
import { RouterLink } from '@/shared/components/RouterLink';
// import { formatRelative } from '@/shared/lib/format';

const isRouteHandle = (value: unknown): value is RouteHandle =>
  typeof value === 'object' && value !== null && 'titleKey' in value;

/** Connects the presentational AppShellTemplate to the router, Redux and server state. */
export const AppLayout = () => {
  const { t } = useTranslation('common');
  const { t: tNav } = useTranslation('navigation');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const matches = useMatches();

  const navMode = useAppSelector(selectNavMode);
  const mobileNavOpen = useAppSelector(selectMobileNavOpen);
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { data: user } = useCurrentUser();
  const { languages, language, setLanguage, label: languageLabel } = useLanguage();
  // const notificationsOpen = useAppSelector(selectNotificationsOpen);
  // const notificationFilter = useAppSelector(selectNotificationFilter);
  // const notifications = useNotifications();

  const navItems = useMemo(() => buildNavItems(tNav), [tNav]);
  const isActive = useCallback((href: string) => pathname === href, [pathname]);
  const titleKey = matches
    .map((m) => m.handle)
    .filter(isRouteHandle)
    .at(-1)?.titleKey;
  // const unreadCount = notifications.data?.filter((n) => n.unread).length ?? 0;

  return (
    <CdAppShellTemplate
      navItems={navItems}
      isActive={isActive}
      linkComponent={RouterLink}
      navMode={navMode}
      onNavModeChange={(mode) => dispatch(setNavMode(mode))}
      mobileNavOpen={mobileNavOpen}
      onMobileNavOpenChange={(open) => dispatch(setMobileNavOpen(open))}
      footerText={t('copyright', { year: new Date().getFullYear() })}
      skipLinkLabel={t('skipToContent')}
      header={{
        title: titleKey ? tNav(titleKey) : t('appName'),
        language: { languages, value: language, onChange: setLanguage, label: languageLabel },
        // unreadCount,
        // onOpenNotifications: () => dispatch(openNotifications()),
        account: isAuthenticated
          ? {
              label: t('account.account'),
              name: user?.fullName,
              email: user?.email,
              initials: user?.initials,
              items: [
                {
                  id: 'account',
                  label: t('account.account'),
                  icon: User,
                  onSelect: () => void navigate(paths.account),
                },
                {
                  id: 'settings',
                  label: t('account.settings'),
                  icon: Settings,
                  onSelect: () => void navigate(paths.settings),
                },
                {
                  id: 'logout',
                  label: t('account.logout'),
                  icon: Logout,
                  divider: true,
                  // TODO(auth): full logout flow (cache cleanup, session timeout) in the logout phase.
                  onSelect: () => void instance.logoutRedirect(),
                },
              ],
            }
          : undefined,
      }}
      // panels={
      //   <CdNotificationsPanel
      //     open={notificationsOpen}
      //     onClose={() => dispatch(closeNotifications())}
      //     filter={notificationFilter}
      //     onFilterChange={(filter) => dispatch(setNotificationFilter(filter))}
      //     linkAs={RouterLink}
      //     seeAllHref={paths.notifications}
      //     labels={{
      //       title: t('notifications.title'),
      //       all: t('notifications.all'),
      //       unread: t('notifications.unread'),
      //       seeAll: t('notifications.seeAll'),
      //       emptyTitle: t('notifications.emptyTitle'),
      //       emptyDescription: t('notifications.emptyDescription'),
      //     }}
      //     items={(notifications.data ?? []).map((n) => ({
      //       id: n.id,
      //       title: n.title,
      //       lines: [n.description, n.applicant, n.permitType],
      //       timestamp: formatRelative(n.createdAt),
      //       unread: n.unread,
      //       href: n.href,
      //       actionLabel: t('notifications.view'),
      //     }))}
      //   />
      // }
    >
      <Outlet />
      <ScrollRestoration />
    </CdAppShellTemplate>
  );
};
